import { KeycloakSingleton } from "../keycloak-provider";
import { assistantAuthHeaders } from "./assistantAuthHeaders";

/**
 * Wraps `fetch` so a 401 from the assistant backend — meaning the RPT/JWT it received has
 * expired or was otherwise rejected — triggers exactly ONE silent token refresh + retry, instead
 * of surfacing the failure to the user. Every assistant fetch call site (chat streaming via
 * DefaultChatTransport, /chat/history, /chat/sessions, /speech-to-text) shares this helper so a
 * session that goes stale mid-conversation self-heals instead of erroring out.
 *
 * `refreshToken` is expected to be `IProperty.refreshToken` (see property-provider.tsx) — it
 * performs a real Keycloak UMA entitlement re-exchange for the current property and returns the
 * new RPT directly, so the retry never has to wait for / rely on a React re-render to see it.
 *
 * Only the auth headers are swapped in on retry — every other header/init option the caller
 * passed is reused verbatim, so this stays agnostic of what a specific call site sends. The
 * user's access token is re-read from the Keycloak singleton at that moment too, since
 * keycloak-js may have rotated it while the original request was in flight.
 *
 * `onRefreshFailed`, when provided, fires the moment the refresh itself fails — i.e. the
 * underlying Keycloak session is gone too, not just the derived RPT, so there is nothing left for
 * this wrapper to retry. Callers use this to distinguish "unrecoverable session expiry" from any
 * other transport error, so the UI can point the user at a reload instead of a generic retry (see
 * ai-assistant-sidebar.tsx's sessionExpired state).
 */
export async function fetchWithTokenRefresh(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  refreshToken: () => Promise<string>,
  fetchImpl: typeof fetch = fetch,
  onRefreshFailed?: () => void
): Promise<Response> {
  const response = await fetchImpl(input, init);
  if (response.status !== 401) return response;

  let freshToken: string;
  try {
    freshToken = await refreshToken();
  } catch (err) {
    // The refresh itself failed (e.g. the underlying Keycloak session is gone too) — there is
    // nothing more this wrapper can do, so surface the ORIGINAL 401 to the caller unchanged.
    console.error("[AIAssistant] Token refresh after 401 failed", err);
    onRefreshFailed?.();
    return response;
  }

  const headers = new Headers(init?.headers);
  const refreshed = assistantAuthHeaders(freshToken, KeycloakSingleton?.token);
  Object.entries(refreshed).forEach(([name, value]) => headers.set(name, value));
  return fetchImpl(input, { ...init, headers });
}
